/**
 * Report Service
 * Wraps all report-related API endpoints.
 */
import apiClient from './api/apiClient';
import { ENDPOINTS, buildEndpoint } from './api/endpoints';
import { getSelectedLanguage, normalizeLanguageCode } from 'utils/language';

export const reportService = {
  listReports: async (params = {}) => {
    const endpoint = buildEndpoint(ENDPOINTS.REPORTS.LIST, params);
    return apiClient.get(endpoint);
  },

  listRecentReports: async (params = {}) => {
    const endpoint = buildEndpoint(ENDPOINTS.REPORTS.RECENTS, params);
    return apiClient.get(endpoint);
  },

  listMatchingReports: async (params = {}) => {
    const endpoint = buildEndpoint(ENDPOINTS.REPORTS.MATCHING, params);
    return apiClient.get(endpoint);
  },

  listReportFields: async (params = {}) => {
    const endpoint = buildEndpoint(ENDPOINTS.REPORTS.FIELDS, params);
    return apiClient.get(endpoint);
  },

  listReportSources: async (params = {}) => {
    const endpoint = buildEndpoint(ENDPOINTS.REPORTS.SOURCES, params);
    return apiClient.get(endpoint);
  },

  listReportSectors: async (params = {}) => {
    const endpoint = buildEndpoint(ENDPOINTS.REPORTS.SECTORS, params);
    return apiClient.get(endpoint);
  },

  listFieldReports: async (fieldId, params = {}) => {
    if (!fieldId) throw new Error('fieldId is required');
    const endpoint = buildEndpoint(ENDPOINTS.REPORTS.FIELD_REPORTS(fieldId), params);
    return apiClient.get(endpoint);
  },

  listSourceReports: async (sourceId, params = {}) => {
    if (!sourceId) throw new Error('sourceId is required');
    const endpoint = buildEndpoint(ENDPOINTS.REPORTS.SOURCE_REPORTS(sourceId), params);
    return apiClient.get(endpoint);
  },

  searchReports: async (params = {}) => {
    const { query, type = 'text', limit = 20, language } = params;

    if (!query || !query.trim()) {
      throw new Error('Search query is required');
    }

    const endpoint = buildEndpoint(ENDPOINTS.REPORTS.SEARCH, {
      query: query.trim(),
      type,
      limit,
      lang: normalizeLanguageCode(language || getSelectedLanguage()),
    });

    return apiClient.get(endpoint);
  },

  getReport: async (reportId, options = {}) => {
    const { language } = options;
    const endpoint = buildEndpoint(ENDPOINTS.REPORTS.DETAIL(reportId), {
      lang: normalizeLanguageCode(language || getSelectedLanguage()),
    });
    return apiClient.get(endpoint);
  },

  getCustomReport: async (reportId, options = {}) => {
    const { language } = options;
    const endpoint = buildEndpoint(ENDPOINTS.REPORTS.CUSTOM_DETAIL(reportId), {
      lang: normalizeLanguageCode(language || getSelectedLanguage()),
    });
    return apiClient.get(endpoint);
  },

  generateReport: async (payload) => {
    return apiClient.post(ENDPOINTS.REPORTS.GENERATE, payload);
  },

  createDisplayReport: async (payload) => {
    return apiClient.post(ENDPOINTS.REPORTS.LIST, payload);
  },

  createCustomization: async (reportId, payload) => {
    const body = {
      ...(payload || {}),
      language: normalizeLanguageCode(payload?.language || getSelectedLanguage()),
    };
    return apiClient.post(ENDPOINTS.REPORTS.CUSTOMIZATION(reportId), body, {
      timeout: 60000,
    });
  },

  createEnhancement: async (customizationId, payload, config = {}) => {
    const body = {
      ...(payload || {}),
      language: normalizeLanguageCode(payload?.language || getSelectedLanguage()),
    };
    return apiClient.post(ENDPOINTS.REPORTS.ENHANCEMENT(customizationId), body, {
      timeout: 120000,
      ...config,
    });
  },

  listCustomReports: async (params = {}) => {
    const endpoint = buildEndpoint(ENDPOINTS.REPORTS.CUSTOM_LIST, params);
    return apiClient.get(endpoint);
  },

  retryCustomReport: async (reportId) => {
    if (!reportId) throw new Error('reportId is required');
    return apiClient.post(ENDPOINTS.REPORTS.CUSTOM_RETRY(reportId), {});
  },

  createCustomReport: async (payload = {}) => {
    const formData =
      payload instanceof FormData
        ? (() => {
            const fd = payload;
            if (!fd.has('language')) {
              fd.append('language', normalizeLanguageCode(getSelectedLanguage()));
            }
            return fd;
          })()
        : (() => {
            const fd = new FormData();
            const {
              prompt,
              detail_level = 'original',
              writing_style = 'original',
              additional_information,
              language,
              attachments = [],
            } = payload || {};

            if (!prompt || !String(prompt).trim()) {
              throw new Error('Prompt is required to generate a report');
            }

            fd.append('prompt', String(prompt).trim());
            fd.append('detail_level', detail_level);
            fd.append('writing_style', writing_style);
            if (additional_information) {
              fd.append('additional_information', additional_information);
            }
            fd.append('language', normalizeLanguageCode(language || getSelectedLanguage()));
            (attachments || []).forEach((file) => {
              if (file) fd.append('attachments', file);
            });
            return fd;
          })();

    return apiClient.post(ENDPOINTS.REPORTS.CUSTOM_CREATE, formData);
  },
};

export default reportService;
